import classNames from 'classnames'
import { FormEvent, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'next-i18next'
import TagManager from 'react-gtm-module'
import css from 'styled-jsx/css'
import { ASvg } from 'ui'
import SvgCheckTrueIcon from 'ui/svg/icons/stroke/check-true.svg'
import SvgCheckFalseIcon from 'ui/svg/icons/stroke/check-false.svg'
import { useSiteInfo } from 'shared'
import { PrismicResultsBody } from '../../prismic-types-d'
import { CUsResult } from './c-us-result'

type Props = {
  className?: string
  quizResult: PrismicResultsBody | null
  finalAnswers: {
    userResponses: Array<{
      question: string
      selectedAnswer: Array<string>
    }>
    quizStructure: Array<{
      question: string
      answers: Array<string>
    }>
  } | null
}

export const CResultTop = ({ className, quizResult, finalAnswers }: Props) => {
  const siteInfo = useSiteInfo()
  const { t } = useTranslation('common')

  const [autopilotForm, setAutopilotForm] = useState<string>('')
  const [autopilotEmail, setAutopilotEmail] = useState<string>('')
  const [autopilotZipcode, setAutopilotZipcode] = useState<string>('')
  const [isConfirmed, setIsConfirmed] = useState<boolean>(false)
  const [isClicked, setIsClicked] = useState<boolean>(false)

  const customFields = useMemo(() => {
    const fields = [
      'string--Quiz--How--Old',
      'string--Quiz--Corrective--Lenses',
      'string--Quiz--Why--Corrective--Lenses',
      'string--Quiz--Journey',
      'string--Quiz--Wearing--Contacts',
      'string--Quiz--Wearing--Glasses',
      'string--Quiz--Night--Vision',
      'string--Quiz--Vision--Correction--Procedure',
      'string--Quiz--Vision--Correction--Price',
    ]

    type Results = Array<[string, string]>

    const results: Results = []

    fields.forEach((val, index) => {
      const selectedAnswer = finalAnswers?.userResponses[index]?.selectedAnswer

      if (selectedAnswer?.length) {
        if (selectedAnswer?.length === 1) {
          results.push([val, selectedAnswer[0]])
        } else {
          selectedAnswer?.forEach((saVal, i) => {
            results.push([`${val}_${i}`, saVal])
          })
        }
      }
    })

    results.push(['string--Quiz--Is--ICL', String(quizResult?.isICL)])

    return Object.fromEntries(results)
  }, [finalAnswers])

  const handleSubmitEmail = async (event: FormEvent) => {
    event.preventDefault()

    setIsClicked(true)

    if ((siteInfo.isUS || siteInfo.isUK) && !isConfirmed) {
      return
    }

    setAutopilotForm('submitting')
    const tempEmail = autopilotEmail

    try {
      const cuRes = await fetch(
        process.env.NEXT_PUBLIC_DISCOVERICL_API_PROXY_URL +
          '/autopilot/captureUser.js',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contact: {
              Email: tempEmail,
              MailingPostalCode: autopilotZipcode,
              MailingCountry: siteInfo.isUK ? 'United Kingdom' : siteInfo.name,
              custom: customFields,
            },
          }),
        }
      )

      if (!cuRes.ok) {
        throw new Error()
      }

      const tjRes = await fetch(
        process.env.NEXT_PUBLIC_DISCOVERICL_API_PROXY_URL +
          '/autopilot/triggerJourney',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            trigger: '0006',
            email: tempEmail,
            MailingCountry: siteInfo.isUK ? 'United Kingdom' : siteInfo.name,
          }),
        }
      )

      if (!tjRes.ok) {
        throw new Error()
      }

      TagManager.dataLayer({
        dataLayer: {
          event: 'vision_report',
        },
      })

      setTimeout(() => {
        setAutopilotForm('success')
      }, 500)

      setTimeout(() => {
        setAutopilotEmail('')
        // document.activeElement.blur()
      }, 2000)
    } catch (e) {
      setAutopilotForm('error')
      setTimeout(() => {
        setAutopilotForm('')
        setAutopilotEmail('')
        // document.activeElement.blur()
      }, 2000)
    } finally {
      pushSegment(tempEmail)
    }
  }

  const pushSegment = (apEmail: string) => {
    if (!window.analytics) {
      return
    }

    const ageRange = finalAnswers?.userResponses[0].selectedAnswer[0]
    const correctiveLenses = finalAnswers?.userResponses[1].selectedAnswer[0]
    const isGlasses = correctiveLenses === 'Glasses'

    window.analytics.track('Personalized Form Requested')
    window.analytics.identify(apEmail, {
      glasses_wearer: isGlasses,
      age_range: ageRange,
      corrective_lenses: correctiveLenses,
      email: apEmail,
    })
  }

  const pushSegmentFinished = () => {
    if (window.analytics) {
      window.analytics.track('Quiz Completed', {
        winner: quizResult?.isICL ? 'isIcl' : 'notIcl',
        result_list: finalAnswers?.userResponses,
      })
    }

    TagManager.dataLayer({
      dataLayer: {
        event: 'Quiz Result',
        winner: quizResult?.isICL ? 'isIcl' : 'notIcl',
      },
    })
  }

  useEffect(() => {
    pushSegmentFinished()
  }, [])

  if (siteInfo.isUS || siteInfo.isUK) {
    return (
      <CUsResult
        quizResult={quizResult}
        handleSubmitEmail={handleSubmitEmail}
        autopilotForm={autopilotForm}
        setAutopilotEmail={setAutopilotEmail}
        autopilotEmail={autopilotEmail}
        setIsConfirmed={setIsConfirmed}
        isConfirmed={isConfirmed}
        isClicked={isClicked}
        autopilotZipcode={autopilotZipcode}
        setAutopilotZipcode={setAutopilotZipcode}
      />
    )
  }

  return (
    <div
      className={`c-result-top ${className} flex flex-col px-5 pt-32 pb-16 md:pb-0 md:px-24`}
    >
      <h2 className="max-w-2xl mb-2 text-h6 font-normal text-neutral-white md:text-neutral-dark-gray">
        {quizResult?.primary.headline_before_result}&nbsp;
        <span
          className={classNames('text-neutral-white', {
            'md:text-primary-teal': quizResult?.isICL,
            'md:text-neutral-dark-gray': !quizResult?.isICL,
          })}
        >
          {quizResult?.primary.procedure_title}
        </span>
      </h2>
      <p className="max-w-xl mb-8 font-semibold text-neutral-white md:text-charcoal-l">
        {quizResult?.primary.result_description}
      </p>
      {(siteInfo.isUS || siteInfo.isUK) && (
        <>
          <h3 className="max-w-xl mb-4 text-lg font-semibold text-primary-teal">
            {quizResult?.primary.email_collection_header}
          </h3>
          <p className="max-w-xl mb-4 font-semibold text-neutral-white md:text-charcoal-l">
            {quizResult?.primary.email_collection_description}
          </p>
          <form
            className="relative flex flex-col justify-between w-full max-w-xl mb-8 md:flex-row"
            onSubmit={(e) => handleSubmitEmail(e)}
          >
            <span
              className={classNames('form__msg form__msg--success', {
                'form__msg--show': autopilotForm === 'success',
              })}
            >
              Your report has been sent!
            </span>
            <span
              className={classNames('form__msg form__msg--error', {
                'form__msg--show': autopilotForm === 'error',
              })}
            >
              There was an error, please try again.
            </span>
            <input
              type="email"
              placeholder={t('enter-your-email') ?? ''}
              required
              className="flex-1 p-3 text-p-sm font-bold bg-transparent bg-neutral-white border-2 placeholder-charcoal md:mie-4 text-charcoal placeholder-charcoal-l"
              aria-label="Enter Your Email"
              onChange={(e) => setAutopilotEmail(e.target.value)}
              value={autopilotEmail}
            />
            <button
              className="px-8 py-4 mt-4 font-bold text-neutral-white uppercase duration-300 rounded-full md:text-sm md:py-1 md:mt-0 bg-primary-teal lg:hover:bg-primary-dark-teal"
              aria-label="Submit"
              type="submit"
              disabled={autopilotForm === 'submitting'}
            >
              {autopilotForm === 'submitting'
                ? `${t('submitting')}...`
                : t('submit')}
            </button>
          </form>
          <div
            className="cursor-pointer flex w-full max-w-xl mb-8"
            onClick={() => setIsConfirmed(!isConfirmed)}
          >
            <ASvg
              svg={isConfirmed ? SvgCheckTrueIcon : SvgCheckFalseIcon}
              className={classNames(
                'flex-shrink-0 fill-current mr-4 text-primary-gray w-[24px] h-[24px]',
                {
                  'text-secondary-red': !isConfirmed && isClicked,
                }
              )}
            />
            <div>
              <p
                className="text-[10px] text-primary-gray w-full mt-0 [&>a]:underline [&>a]:text-primary-dark-teal"
                dangerouslySetInnerHTML={{
                  __html: t('form-subscription-text') ?? '',
                }}
              />
              {!isConfirmed && isClicked && (
                <p className="text-xs font-bold text-secondary-red mt-1">
                  Checkbox is required.
                </p>
              )}
            </div>
          </div>
        </>
      )}
      <style jsx>{resultStyles}</style>
    </div>
  )
}

export const resultStyles = css`
  @media screen and (max-width: 1112px) {
    .c-result-top {
      grid-column: 1 / 1;
      grid-row: 1 / 1;
    }
  }

  .form__msg {
    position: absolute;
    background-color: #fff;
    transition: all 0.3s linear;
    font-weight: bold;
    top: 2px;
    inset-inline-start: 2px;
    height: 42px;
    display: flex;
    align-items: center;
    padding-inline-start: 12px;
    width: 75%;
    opacity: 0;
    pointer-events: none;
  }

  .form__msg--success {
    color: #10a8ba;
  }

  .form__msg--error {
    color: #b00020;
  }

  .form__msg--show {
    opacity: 1;
  }
`
